"use client";

import Link from "next/link";
import { toolsRegistry } from "@/contexts/ToolsContext";
import { trackFunnel } from "@/libs/analytics";
import {
  buildPresetHref,
  getPhotoEditorPreviewPoster,
  type PhotoEditorPreview,
  type PhotoEditorTool,
} from "@/app/editor/data/ai-photo-editor";
import FadeCyclePreview from "./FadeCyclePreview";
import BeforeAfterCyclePreview from "./BeforeAfterCyclePreview";

function fallbackImage(tool: PhotoEditorTool): string | undefined {
  return (
    getPhotoEditorPreviewPoster(tool.preview) ??
    toolsRegistry.find((t) => t.id === tool.mapsToToolId)?.imageUrl
  );
}

function PreviewMedia({ preview, alt }: { preview?: PhotoEditorPreview; alt: string }) {
  if (preview?.type === "fade-cycle" && preview.images.length > 0) {
    return (
      <div className="absolute inset-0">
        <FadeCyclePreview
          images={preview.images}
          maxFrames={preview.images.length}
          alt={alt}
          className="h-full w-full"
        />
      </div>
    );
  }
  if (preview?.type === "before-after" && preview.pairs.length > 0) {
    return (
      <div className="absolute inset-0">
        <BeforeAfterCyclePreview pairs={preview.pairs} alt={alt} className="h-full w-full" />
      </div>
    );
  }
  if (preview?.type === "static" && preview.image) {
    return (
      <img
        src={preview.image}
        alt={alt}
        className="absolute inset-0 h-full w-full object-cover object-top"
        loading="lazy"
        decoding="async"
      />
    );
  }
  return null;
}

export default function CatalogToolCard({
  tool,
  compact = false,
  fill = false,
}: {
  tool: PhotoEditorTool;
  compact?: boolean;
  fill?: boolean;
}) {
  const href = buildPresetHref(tool.id);
  const hasPreview =
    (tool.preview?.type === "fade-cycle" && tool.preview.images.length > 0) ||
    (tool.preview?.type === "before-after" && tool.preview.pairs.length > 0) ||
    (tool.preview?.type === "static" && Boolean(tool.preview.image));
  const fallback = hasPreview ? undefined : fallbackImage(tool);

  return (
    <Link
      href={href}
      onClick={() =>
        trackFunnel("tool_engagement", "dashboard_card_clicked", {
          tool_id: "ai-photo-editor",
          card_type: "preset",
          card_id: tool.id,
          section: tool.categoryId,
          destination_href: href,
        })
      }
      className={`group flex flex-col ${fill ? "lg:h-full lg:min-h-0" : "h-full"}`}
    >
      <div
        className={`relative w-full overflow-hidden rounded-2xl bg-gray-100 ${
          fill ? "aspect-[2/3] lg:aspect-auto lg:min-h-0 lg:flex-1" : "aspect-[2/3]"
        }`}
      >
        {fallback ? (
          <img
            src={fallback}
            alt={tool.title}
            className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            decoding="async"
          />
        ) : null}
        <div className="absolute inset-0 transition-transform duration-500 group-hover:scale-105">
          <PreviewMedia preview={tool.preview} alt={tool.title} />
        </div>
        {!fallback && !hasPreview && (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
            <span className="text-3xl font-bold text-gray-300">{tool.title.slice(0, 1)}</span>
          </div>
        )}
      </div>
      <div className={compact ? "pt-2" : "pt-3"}>
        <h3 className="text-base font-bold leading-tight text-gray-900">{tool.title}</h3>
        {!compact ? (
          <p className="mt-1 line-clamp-2 text-sm leading-snug text-gray-500">{tool.tagline}</p>
        ) : null}
      </div>
    </Link>
  );
}
